<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<template>
  <div class="form-view container pb-5">
    <Breadcrumbs />
    <h2 class="h-medium">Informe seus dados</h2>
    <p>Confira seus dados antes de criar sua senha de emissão</p>

    <div class="card p-2 d-flex flex-row flex-wrap">
      <div class="ms-2">
        <span class="fw-600 capiton">Certificado Adquirido</span>:
        {{ getProduto.nome }}
      </div>
    </div>
    <DynamicForms
      :key="dynamicCount"
      ref="dynamicComponent"
      :fields="form.fields"
      :external-errors="{ email: emailOwnership.fieldError }"
      @isValid="handleIsValid"
    />

    <h2>Senha de emissão</h2>
    <p>Agora você precisa criar sua senha de emissão</p>
    <div class="alert alert-dismissible alert-info" role="alert">
      <div class="alert__icon mt-2">
        <Icon name="lightbulb-on" />
      </div>
      <div class="alert__content">
        <div class="text">
          A senha deve ter entre <b>8 e 12 caracteres</b>, ter pelo menos uma
          letra <b>maiúscula</b>, uma letra <b>minúscula</b> e <b>números</b>.
          <br />
          <b
            >Guarde sua senha em um local seguro pois não será possível
            alterar.</b
          >
        </div>
      </div>
    </div>
    <div class="password-wrapper form-container mt-3">
      <div class="input-create-password form-item">
        <Input
          v-model="model.password"
          label="Senha"
          icon="lock"
          :is-required="true"
          placeholder="Digite a senha"
          type="password"
        />
        <div class="d-flex justify-content-end mt-2">
          <span
            class="circle"
            :class="[
              validation.minLength ? 'bg--success' : 'bg--grayscale-light',
            ]"
          >
            +8
          </span>
          <span
            class="mx-2 circle"
            :class="[
              validation.uppercase ? 'bg--success' : 'bg--grayscale-light',
            ]"
          >
            A
          </span>
          <span
            class="circle"
            :class="[
              validation.lowercase ? 'bg--success' : 'bg--grayscale-light',
            ]"
          >
            a
          </span>
          <span
            class="ms-2 circle"
            :class="[
              validation.hasNumber ? 'bg--success' : 'bg--grayscale-light',
            ]"
          >
            123
          </span>
        </div>
      </div>
      <div class="input-confirm-password form-item">
        <Input
          v-model="model.confirmPassword"
          label="Confirmação de senha"
          icon="lock"
          :is-required="true"
          placeholder="Confirme a senha"
          type="password"
          :error-log="errorPassword"
        />
      </div>
    </div>

    <div class="d-flex justify-content-end my-4 row p-2">
      <button
        class="col-sm-12 col-md-2 btn btn-primary"
        type="button"
        :class="
          !isValid || !isValidPassword || emailOwnership.isVerifying
            ? 'btn-secondary'
            : 'btn-primary'
        "
        :disabled="!isValid || !isValidPassword || emailOwnership.isVerifying"
        @click="finishJourney"
      >
        <span class="me-2">Avançar</span>
        <Icon name="arrow-right" />
      </button>
    </div>
    <Contact />
  </div>
  <ModalValidationBirdSpro
    ref="modalValidationBirdSpro"
    :hotreload="
      () => {
        dynamicCount++
      }
    "
  />
  <ModalEmailOwnershipBlocked
    :show="emailOwnership.showBlockedModal"
    :on-close="emailOwnership.closeBlockedModal"
  />
  <ModalEmailOwnershipOtp
    :show="emailOwnership.showOtpModal"
    :email="dynamicComponent?.model?.email || ''"
    :otp-prefix="emailOwnership.otpPrefix"
    :loading="emailOwnership.isVerifying"
    :error-message="emailOwnership.otpError"
    :on-close="emailOwnership.closeOtpModal"
    @submit="emailOwnership.submitOtp"
    @resend="emailOwnership.resendCode"
    @change-email="emailOwnership.closeOtpModal"
  />
  <ModalEmailOwnershipGenericError
    :show="emailOwnership.showGenericErrorModal"
    :message="emailOwnership.genericErrorMessage"
    :on-close="emailOwnership.closeGenericErrorModal"
  />
</template>
<script lang="ts">
  import { ComponentOptions, defineComponent, ref } from 'vue'
  import DynamicForms from '@/components/Molecules/DynamicForms.vue'
  import ModalEmailOwnershipBlocked from '@/components/templates/ModalEmailOwnershipBlocked.vue'
  import ModalEmailOwnershipOtp from '@/components/templates/ModalEmailOwnershipOtp.vue'
  import ModalEmailOwnershipGenericError from '@/components/templates/ModalEmailOwnershipGenericError.vue'
  import { mapActions, mapState } from 'pinia'
  import {
    STATE_DEFAULT,
    useSolicitation,
  } from '@/store/Pinia/useSolicitation.store'
  import {
    FieldType,
    dynamicFormsController,
  } from '@/infra/sisAr/dynamicForms.controller'
  import { useRouteBirdId } from '@/store/Pinia/useRouteBirdId.store'
  import {
    FormDataType,
    useDynamicForms,
  } from '@/store/Pinia/useDynamicForms.store'
  import { useAR } from '@/store/Pinia/useAR.store'
  import ModalValidationBirdSpro from '@/components/templates/ModalValidationBirdSpro.vue'
  import { ApplyMaskToCPF, RemoveMaskCPF } from '@/utils/formatDocument'
  import { useModalLoading } from '@/store/Pinia/useModalLoading.store'
  import { useMainStore } from '@/store/Pinia/useMain.store'
  import { useToastGlobal } from '@/store/Pinia/useToastGlobal.store'
  import {
    FinishJourneyAndEmitType,
    finishJourneyAndEmit,
  } from '@/utils/finishJourney'
  import emissionController from '@/infra/BirdId/controllers/emission.controller'
  import { ESolicitationStatus } from '@/types/enums/ESisar.enum'
  import getMunicipioByNameAndUf from '@/utils/getMunicipioByNameAndUF'
  import { useOeRecaptcha } from '@/composables/useOeRecaptcha'
  import { useEmailOwnershipVerification } from '@/composables'

  type ParamsRoute = {
    arsubdomain: string
  }

  type QueryStringParamsRoute = {
    i: string
    d: string
    s: string
    v: string
    data: any
  }

  export default defineComponent({
    name: 'FormDataSolutiPro',
    components: {
      DynamicForms,
      ModalValidationBirdSpro,
      ModalEmailOwnershipBlocked,
      ModalEmailOwnershipOtp,
      ModalEmailOwnershipGenericError,
    },
    setup() {
      const dynamicComponent = ref<ComponentOptions | null>(null)
      const emailOwnership = useEmailOwnershipVerification()

      return {
        dynamicComponent,
        emailOwnership,
        oeRecaptcha: useOeRecaptcha(),
      }
    },
    data: () => ({
      form: {
        codVoucher: '',
        fields: [{}] as FieldType[],
        isPj: false,
        isSSL: false,
      },
      model: {
        password: '',
        confirmPassword: '',
      },
      validation: {
        uppercase: false,
        lowercase: false,
        hasNumber: false,
        minLength: false,
      },
      errorPassword: '',
      isValid: false,
      dynamicCount: 0,
      error: '',
    }),
    watch: {
      'model.password': function w(current: string) {
        const lastLetter = current.slice(-1)

        if (/[\\"/'/`/´]/g.test(lastLetter)) {
          this.errorPassword = 'caractere inválido'
        } else {
          this.errorPassword = ''
        }

        const lowerCaseLetters = /[a-z]/g
        if (current.match(lowerCaseLetters)) {
          this.validation.lowercase = true
        } else {
          this.validation.lowercase = false
        }
        const upperCaseLetters = /[A-Z]/g
        if (current.match(upperCaseLetters)) {
          this.validation.uppercase = true
        } else {
          this.validation.uppercase = false
        }
        const numbers = /[0-9]/g
        if (current.match(numbers)) {
          this.validation.hasNumber = true
        } else {
          this.validation.hasNumber = false
        }
        if (current.length >= 8) {
          this.validation.minLength = true
        } else {
          this.validation.minLength = false
        }

        if (current.length > 0 && this.model.confirmPassword) {
          this.checkSamePassword()
        }
      },
      'model.confirmPassword': function w(current: string) {
        if (current.length > 0) {
          this.checkSamePassword()
        }
      },
    },
    methods: {
      ...mapActions(useDynamicForms, [
        'setFormData',
        'setFormInfo',
        'setPassword',
      ]),
      ...mapActions(useRouteBirdId, ['setRouterDone']),
      ...mapActions(useModalLoading, ['setIsLoading']),
      ...mapActions(useToastGlobal, ['toggleToastGlobal']),
      async saveAndNavigate() {
        if (this.isValid) {
          const modelDynamic = this.$refs.dynamicComponent as ComponentOptions
          this.setFormData(modelDynamic.model as FormDataType)
          this.$router.push('/birdId/pass')
          this.setRouterDone('form')
        }
      },
      handleIsValid(value: boolean) {
        this.isValid = value
      },
      openModal() {
        const modal = this.$refs
          .modalValidationBirdSpro as typeof ModalValidationBirdSpro
        modal.modalOpen()
      },
      checkSamePassword() {
        if (this.model.password !== this.model.confirmPassword) {
          this.errorPassword = 'As senhas não correspondem.'
        } else {
          this.errorPassword = ''
        }
      },
      async finishJourney() {
        const modelDynamicPre = this.$refs.dynamicComponent as ComponentOptions
        const formModel = modelDynamicPre.model as FormDataType

        const verified = await this.emailOwnership.verify({
          email: formModel.email,
          identity: RemoveMaskCPF(this.getFormData.cpf || ''),
          recipientFullName: this.getFormData.nome || '',
        })

        if (!verified) {
          return
        }

        this.setIsLoading({
          isLoading: true,
          title: 'Aguarde um pouquinho! Estamos enviando sua Solicitação',
          image: 'clock',
        })

        try {
          const modelDynamic = this.$refs.dynamicComponent as ComponentOptions
          this.setFormData(modelDynamic.model as FormDataType)

          const municipio = getMunicipioByNameAndUf(
            this.formData.municipio,
            this.formData.uf,
          )

          if (!municipio) {
            throw new Error('Não foi possível encontrar o município')
          }

          const telefoneFormatted = this.formData.telefone
            .replace('55', '')
            .replace(/[^0-9]/g, '')

          const formattedFormData = { ...this.getFormData }

          Object.entries(this.getFormData).forEach(([key, value]) => {
            if (value === '')
              delete formattedFormData[key as keyof FormDataType]
          })

          const finishJourneyAndEmitParams = {
            sessionData: this.getSolicitationToken.sessionData,
            sessionId: this.getSolicitationToken.sessionId,
            formData: {
              ...formattedFormData,
              senha: this.model.password,
              senhaConfirm: this.model.confirmPassword,
              municipio: municipio.idmunicipio,
              telefone: telefoneFormatted,
              email: this.formData.email.toLowerCase(),
              uf: this.formData.uf,
              cnpj: (this.formData.cnpj || '')?.replace(/[^\w]/g, ''),
              razaoSocial: this.formData.razao_social || '',
              idunidade: this.idunidade,
              isRFB: this.getSolicitation.isRFB,
              razao_social: this.formData.razao_social || '',
            },
          } as FinishJourneyAndEmitType

          const response = await finishJourneyAndEmit(
            finishJourneyAndEmitParams,
          )

          const recaptchaTokenGetSolicitationData =
            await this.oeRecaptcha.getToken('getsolicitationdata')

          const solicitationData = await emissionController.getSolicitationData(
            {
              solicitacao: response.codigioSolicitacao,
              arsubdomain: this.arData.subdomain || '',
              recaptcha_token: recaptchaTokenGetSolicitationData,
            },
          )

          this.setPassword(this.model.password)

          if (
            Number(solicitationData.situacao.id) ===
            ESolicitationStatus.APROVADA
          ) {
            this.$router.push({ name: 'ReservedDocument' })
            return
          }

          if (response.isQsa && this.getSolicitation.isSSL) {
            this.$router.push({ path: '/domain-validation' })
            return
          }

          if (!this.getSolicitation.isPj && (this.hasCei || this.hasPis)) {
            this.$router.push({ path: '/upload-documents-pis-cei' })
            return
          }

          if (!this.getSolicitation.isPj || response.isQsa) {
            const route = this.getSolicitation.isBirdId
              ? '/installation-guide-birdid'
              : '/installation-guide-token'
            this.$router.push({ path: route })
            return
          }

          this.$router.push({ path: '/upload-documents' })

          return
        } catch (_error) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const err = _error as any
          this.error = err.message
          let errorMessage =
            'Houve um erro ao tentar atualizar os dados da solicitação'
          if (err.response) {
            if (
              err.response.data.message &&
              err.response?.data.message !== ''
            ) {
              errorMessage = err.response?.data.message
            }
          }

          this.toggleToastGlobal({
            type: 'danger',
            isShow: true,
            message: errorMessage,
          })
          this.setIsLoading({ isLoading: false })
        } finally {
          setTimeout(() => {
            this.setIsLoading({ isLoading: false })
          }, 300)
        }
      },
    },
    computed: {
      ...mapState(useDynamicForms, ['formData', 'getFormData', 'getPassword']),
      ...mapState(useSolicitation, [
        'getProduto',
        'getSolicitation',
        'getSolicitationToken',
      ]),
      ...mapState(useAR, ['arData']),
      ...mapState(useMainStore, ['idunidade']),
      hasCei(): boolean {
        return this.getFormData.cei !== ''
      },
      hasPis(): boolean {
        return this.getFormData.pis !== ''
      },
      isValidPassword() {
        // eslint-disable-next-line object-curly-newline
        const { uppercase, lowercase, hasNumber, minLength } = this.validation
        return (
          // eslint-disable-next-line operator-linebreak
          uppercase &&
          // eslint-disable-next-line operator-linebreak
          lowercase &&
          // eslint-disable-next-line operator-linebreak
          hasNumber &&
          // eslint-disable-next-line operator-linebreak
          minLength &&
          // eslint-disable-next-line operator-linebreak
          this.model.confirmPassword &&
          !this.errorPassword
        )
      },
    },
    mounted() {
      this.form.isPj = this.getSolicitation.isPj
      this.form.isSSL = this.getSolicitation.isSSL

      this.oeRecaptcha.showBadge()

      this.openModal()
    },
    async beforeCreate() {
      const solicitation = useSolicitation()
      const produto = solicitation.getProduto
      const main = useMainStore()
      main.fetchEnviroment()

      const arsubdomain =
        main.enviroment === 'HOM' || main.enviroment === 'DEV'
          ? 'arsolutihom'
          : 'arsoluti'

      const model = {
        productId: produto.id,
        sku: produto.sku,
        arsubdomain: arsubdomain || '',
      }

      const { fields, produto: productName } =
        await dynamicFormsController.getFields(model)

      solicitation.setProduto({
        ...solicitation.getProduto,
        nome: productName,
      })

      let formattedFields = [...fields]

      formattedFields = formattedFields.map((field) => {
        if (
          // eslint-disable-next-line operator-linebreak
          field.id === 'cnpj' ||
          field.id === 'razao_social'
        ) {
          return {
            ...field,
            disable: true,
            order: field.id === 'cnpj' ? -2 : -1,
          }
        }
        return { ...field }
      })

      const fieldsOrdened = formattedFields
        .sort((a: FieldType, b: FieldType) => (a.order < b.order ? -1 : 1))
        .sort((x: FieldType) => (x.disable ? -1 : 1))

      this.form.fields = fieldsOrdened as FieldType[]

      const fieldsInfo = fields.map((field: FieldType) => ({
        id: field.id,
        icon: field.icon,
        label: field.label,
      }))

      this.setFormInfo(fieldsInfo)
      this.dynamicCount += 1
    },
    beforeRouteLeave() {
      this.oeRecaptcha.hideBadge()
    },
    async beforeRouteEnter(to, _, next) {
      const dynamicForms = useDynamicForms()
      const solicitation = useSolicitation()
      const ar = useAR()

      dynamicForms.clearData()

      const { arsubdomain } = to.params as ParamsRoute

      const {
        d: cpfBase64,
        i: productId,
        s: sku,
      } = to.query as QueryStringParamsRoute

      if (!arsubdomain || !cpfBase64 || !productId || !sku) {
        return next({ name: 'WithoutRoute' })
      }

      const cpf = window.atob(cpfBase64)

      await ar.fetchAraData(arsubdomain)

      solicitation.setEncryptedData({
        iv: to.query.v as string,
        data: decodeURIComponent(to.query.data as string),
      })

      solicitation.setProduto({
        ...STATE_DEFAULT.produto,
        id: window.atob(productId),
        sku: window.atob(sku),
      })

      dynamicForms.setCPF(ApplyMaskToCPF(cpf))
      return next()
    },
  })
</script>

<style lang="stylus" scoped>
  @import './style.modules.styl';
</style>
